import React, { useEffect, useState } from "react";

import ConfirmDeleteModal from "../components/common/ConfirmDeleteModal";
import MemberModal from "../components/member/MemberModal";
import DataTable from "../components/common/DataTable";
import { api_paths } from "../config/api.paths";
import API from "../config/axios";
import { useDispatch, useSelector } from "react-redux";
import { formatToUSDate } from "../utils/dateFormat";
import { SET_LOADING } from "../redux/user";
import LeftArrow from "../assets/images/committee/ArrowLeft.svg";
import { useNavigate } from "react-router-dom";

const StatusBadge = ({ status }) => {
  const classes = {
    Active: "badge bg-success-subtle",
    Free: "badge bg-warning-subtle",
    Expired: "badge bg-danger-subtle",
    Pending: "badge bg-danger-subtle",
  };

  return <span className={`${classes[status]} px-3`}>{status}</span>;
};

function RegisterMembership() {
  const [members, setMembers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const { loading } = useSelector((state) => state.USER_DATA);

  const { user } = useSelector((state) => state.USER_DATA);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const customPrimaryRow = {
    _id: "primary-custom-row",
    first_name: `${user?.first_name || ""} ${user?.last_name || ""}`,
    date_of_birth: formatToUSDate(user?.date_of_birth),
    gender: user?.gender ? user.gender.charAt(0).toUpperCase() + user.gender.slice(1) : "",
    marital_status: user?.marital_status || "",
    relationship: "Primary",
    is_membership_active: user?.is_membership_active || null,
    membership_id: user?.membership_id || null,
  };

  const fetchMembers = async () => {
    try {
      dispatch(SET_LOADING(true));
      const response = await API.get(api_paths.member.family_member.GET_MEMBER);

      if (response.status === 200) {
        setMembers(response?.data?.payload?.result || []);
      }
    } catch (error) {
      console.error("Error fetching members:", error);
    } finally {
      dispatch(SET_LOADING(false)); // STOP LOADING
    }
  };

  const getMembershipStatus = (m) => {
    if (m?.is_free_membership) return "Free";
    if (m?.is_membership_active) return "Active";

    const expiryDate = m?.membership_expiry_date;

    // If expiryDate is null or invalid, return "Pending"
    if (!expiryDate || isNaN(new Date(expiryDate))) return "Pending";

    // Compare valid expiry date with the current date
    const expiry = new Date(expiryDate);
    return expiry < new Date() ? "Expired" : "Pending";
  };

  const capitalize = (val) =>
    typeof val === "string" && val.length ? val.charAt(0).toUpperCase() + val.slice(1) : val || "";

  const membershipColumns = [
    {
      key: "first_name",
      label: "Name",
      render: (_, row) => `${row.first_name || ""} ${row.last_name || ""}`,
    },
    {
      key: "date_of_birth",
      label: "Date of Birth",
      render: (val) => formatToUSDate(val),
    },
    {
      key: "gender",
      label: "Gender",
      render: (val) => capitalize(val),
    },
    { key: "relationship", label: "Relation" },
    {
      key: "membership_status",
      label: "Membership",
      render: (_, row) => <StatusBadge status={getMembershipStatus(row)} />,
    },
  ];

  const handleDelete = async (row) => {
    setSelectedRow(row);
    setShowDeleteModal(true);
  };
  const handleDeleteConfirmed = async (row) => {
    if (!row?._id || row.relationship === "Primary") return;

    try {
      dispatch(SET_LOADING(true));
      await API.delete(`${api_paths.member.family_member.DELETE_MEMBER}/${row._id}`);

      const response = await API.get(api_paths.member.family_member.GET_MEMBER);

      setMembers(response.data.payload.result || []);
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Failed to delete member. Try again.");
    } finally {
      dispatch(SET_LOADING(false));
    }
  };
  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line
  }, []);

  return (
    <div>
      <div className="container pb-5 pt-4">
        <div className="row d-flex mb-4 mx-auto">
          <div className="col-lg-8 d-flex align-items-center px-0">
            <img
              className="cursor-pointer"
              src={LeftArrow}
              alt="back-arrow"
              onClick={() => {
                navigate("/dashboard");
              }}
            />
            <h1 className="mb-0 fw-bold text-main f-20 ms-3">
              Family Members ({members.length + 1})
            </h1>
          </div>
          <div className="col-lg-4 d-flex justify-content-end px-0">
            <button
              className="btn btn-orange-md fw-medium fs-14"
              onClick={() => {
                setEditData(null);
                setShowModal(true);
              }}
            >
              Add Member
            </button>
          </div>
        </div>
        <div className="row mx-auto">
          <div className="col-lg-12 px-0">
            <DataTable
              columns={membershipColumns}
              data={[customPrimaryRow, ...members]}
              loading={loading}
              onEdit={(row) => {
                if (row.relationship !== "Primary") {
                  setEditData(row);
                  setShowModal(true);
                }
              }}
              onDelete={handleDelete}
            />
          </div>
        </div>
      </div>

      <MemberModal
        show={showModal}
        onHide={() => setShowModal(false)}
        editData={editData}
        refreshList={fetchMembers}
      />

      <ConfirmDeleteModal
        show={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={() => {
          handleDeleteConfirmed(selectedRow);
          setShowDeleteModal(false);
        }}
      />
    </div>
  );
}

export default RegisterMembership;
